import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  HandlersUserDetailResponse,
  usePatchApiUsersByIdMutation,
} from '../../../../app/athenaApiGen';
import { Role, UserRoleWithScopes, ZERO_GUID } from '../../Types';
import { RolesPanel } from './components/RolesPanel';
import SelectedUserDisplay from './components/SelectedUserDisplay';
import UserTable from './components/UserTable';

export default function UserSettings() {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftColumnWidth, setLeftColumnWidth] = useState(50); // percentage
  // When the right (permissions) panel is expanded, we shrink the left
  // column to almost nothing and hide the drag handle so the roles panel
  // can use the full width. `previousLeftWidth` restores whatever split
  // the user had before expanding.
  const [rightExpanded, setRightExpanded] = useState(false);
  const [previousLeftWidth, setPreviousLeftWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const toggleRightExpanded = useCallback(() => {
    setRightExpanded((prev) => {
      if (!prev) {
        setPreviousLeftWidth(leftColumnWidth);
        setLeftColumnWidth(0);
      } else {
        setLeftColumnWidth(previousLeftWidth || 50);
      }
      return !prev;
    });
  }, [leftColumnWidth, previousLeftWidth]);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      // Clamp between 20% and 80%
      setLeftColumnWidth(Math.min(80, Math.max(20, newWidth)));
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // State
  const [selectedUser, setSelectedUser] =
    useState<HandlersUserDetailResponse | null>(null);
  const [newUser, setNewUser] = useState<{
    userName: string;
    userEmail: string;
    roles: Role[];
  }>({
    userName: '',
    userEmail: '',
    roles: [],
  });
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<
    'success' | 'error'
  >('success');
  const [userRolesWithScopes, setUserRolesWithScopes] =
    useState<UserRoleWithScopes>([]);

  const [saveUser] = usePatchApiUsersByIdMutation();

  const handleSelectUser = (user: HandlersUserDetailResponse) => {
    if (selectedUser?.id !== user.id) {
      setSelectedUser(user);
    }
  };

  const disableApply = useMemo(() => {
    let disable = false;
    userRolesWithScopes.some((userRole) => {
      if (userRole.programs.length === 0 && userRole.projects.length === 0) {
        disable = true;
        return true; //breaks from the loop
      } else {
        return false;
      }
    });

    return disable;
  }, [userRolesWithScopes]);

  const handleApply = async () => {
    if (!selectedUser?.id || !userRolesWithScopes) return;

    try {
      // Build roles payload
      const rolesPayload: Array<{
        programId: string;
        roleName: string;
        projectId?: string;
      }> = [];

      userRolesWithScopes.forEach((userRoleObject) => {
        if (userRoleObject.role.roleName === undefined) return;
        if (
          userRoleObject.role.scope?.toLocaleLowerCase() === 'project' &&
          userRoleObject.projects.length > 0
        ) {
          const allEntry = userRoleObject.projects.find(
            (project) => project.projectId === ZERO_GUID,
          );
          if (allEntry) {
            // "All" selected → grant a program-level role only (no projectId).
            // The backend interprets a program-level role with no project
            // entries as access to all current and future projects in that
            // program.
            if (allEntry.programId) {
              rolesPayload.push({
                programId: allEntry.programId,
                roleName: userRoleObject.role.roleName,
              });
            } else {
              console.warn(
                'All projects selected but no programId available for role:',
                userRoleObject.role.roleName,
              );
            }
          } else {
            const done = new Set<string>();
            // program entry for project role
            if (
              userRoleObject.projects[0].programId &&
              !done.has(userRoleObject.projects[0].programId)
            ) {
              rolesPayload.push({
                programId: userRoleObject.projects[0].programId,
                roleName: userRoleObject.role.roleName,
              });
              done.add(userRoleObject.projects[0].programId);
            }
            // project entries for project role
            userRoleObject.projects.forEach((project) => {
              if (project.projectId && project.programId) {
                rolesPayload.push({
                  programId: project.programId,
                  roleName: userRoleObject.role.roleName as string, // this is already checked above
                  projectId: project.projectId,
                });
              }
            });
          }
        } else if (
          userRoleObject.role.scope?.toLocaleLowerCase() === 'program' &&
          userRoleObject.programs.length > 0
        ) {
          // program entries for program role
          userRoleObject.programs.forEach((program) => {
            if (program.programId) {
              rolesPayload.push({
                programId: program.programId,
                roleName: userRoleObject.role.roleName as string, // this is already checked above
              });
            }
          });
        } else if (userRoleObject.role.scope === 'system') {
          // system role
          console.log(
            'System role found but not handled: ',
            userRoleObject.role.roleName,
          );
        }
      });

      const payload = {
        contact: {
          address: selectedUser.contact?.address,
          govEmail: selectedUser.contact?.govEmail,
          name: selectedUser.contact?.name || '',
          personalPhone: selectedUser.contact?.personalPhone,
          workEmail: selectedUser.contact?.workEmail,
          workPhone: selectedUser.contact?.workPhone,
        },
        roles: rolesPayload,
        userEmail: selectedUser.userEmail,
        userType: selectedUser.userType,
      };

      await saveUser({
        id: selectedUser.id,
        handlersUpdateUserRequest: payload,
      }).unwrap();
      handleNotification('User roles updated successfully');
    } catch (error: any) {
      console.error('Error updating user roles:', error);
      handleNotification(
        error.data?.details || 'Error updating user roles',
        'error',
      );
    }
  };

  const handleNotification = (
    message: string,
    severity: 'success' | 'error' = 'success',
  ) => {
    setNotificationMessage(message);
    setNotificationSeverity(severity);
    setNotificationOpen(true);
  };

  const handleNotificationClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') return;
    setNotificationOpen(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        // Cap the panel so its internal columns can actually scroll within
        // it (otherwise the whole page scrolls and the sticky Apply button
        // never has a viewport to stick to).
        maxHeight: '75vh',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {/* Main Content - 2 Column Layout */}
      <Box
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          userSelect: isDragging ? 'none' : 'auto',
        }}
      >
        {/* Left Column - Users Table */}
        {!rightExpanded && (
          <Box
            sx={{
              width: `${leftColumnWidth}%`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              minWidth: 0,
              px: 2,
            }}
          >
            <Paper
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor:
                  theme.palette.mode === 'light' ? 'transparent' : 'inherit',
              }}
            >
              <UserTable
                newUser={newUser}
                setNewUser={setNewUser}
                setParentSelectedUser={handleSelectUser}
                onNotification={handleNotification}
              />
            </Paper>
          </Box>
        )}

        {/* Resizable Handle (hidden while right panel is expanded) */}
        {!rightExpanded && (
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              width: '8px',
              cursor: 'col-resize',
              backgroundColor: isDragging ? 'primary.main' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mx: 1,
            }}
          >
            <Box
              sx={{
                width: '2px',
                height: '40px',
                backgroundColor: '#B8B8B8',
                borderRadius: '1px',
              }}
            />
          </Box>
        )}

        {/* Right Column - SELECTED USER & Roles */}
        <Box
          sx={{
            width: rightExpanded ? '100%' : `${100 - leftColumnWidth}%`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            minWidth: 0,
            px: 2,
          }}
        >
          {/* Selected User Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              width: '100%',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SelectedUserDisplay selectedUser={selectedUser} />
            </Box>
            <Tooltip
              title={rightExpanded ? 'Show users list' : 'Expand to full width'}
            >
              <IconButton size="small" onClick={toggleRightExpanded}>
                {rightExpanded ? (
                  <CloseFullscreenIcon fontSize="small" />
                ) : (
                  <OpenInFullIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Roles Panel */}
          <RolesPanel
            selectedUser={selectedUser}
            setParentUserRolesWithScopes={setUserRolesWithScopes}
          />
        </Box>
      </Box>

      {/* Apply row — full-width row below the two-column layout. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          pt: 2,
          pb: 0,
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          size="small"
          variant="contained"
          sx={{
            background: 'rgb(31, 77, 131)',
            '&:hover': {
              background: 'rgb(0, 136, 255)',
            },
            color: (theme) => theme.palette.secondary.contrastText,
            textTransform: 'none',
          }}
          disabled={!selectedUser || disableApply}
          onClick={handleApply}
        >
          Apply
        </Button>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notificationOpen}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notificationSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
